/**
 * Per-policy tooltip renderers for StrategyChart.
 * Each renderer takes the raw data row, chart state, and returns
 * { labels, values, colors, formulas, subtitles?, footer? } for renderCoverageTooltipHTML.
 */

import { DatasetConfig } from './StrategyChart';

export interface TooltipResult {
  labels: string[];
  values: string[];
  colors: string[];
  formulas: string[];
  subtitles?: string[];
  footer?: string;
  separators?: number[]; // Row indices where a separator line appears BEFORE that row
  html?: string; // When set, bypasses label/value rendering and uses this HTML directly
}

// Helper: inline colored dot
const dot = (color: string) =>
  `<span class="inline-block w-2 h-2 rounded-sm align-middle" style="background:${color}"></span>`;

// ---------- Buffer ----------
export function renderBufferTooltip(
  dataIndex: number,
  bufferRows: any[],
  isQuantity: boolean,
  matPrice: number,
  supplyTerm: string,
  demandTerm: string,
): TooltipResult {
  const fmt = (v: number) => {
    const raw = isQuantity ? v : v * matPrice;
    const abs = Math.abs(raw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return isQuantity ? abs : `$${abs}`;
  };
  const empty: TooltipResult = { labels: [], values: [], colors: [], formulas: [] };

  const isOnHand = dataIndex === 0;
  if (isOnHand) {
    const onHandStorage = bufferRows[0]?.storage || 0;
    return {
      ...empty,
      html: `
        <div class="flex items-center gap-2">
          <div class="w-3 h-3 rounded-sm" style="background:#10B981"></div>
          <span class="text-sm font-medium text-gray-700">On-Hand Storage</span>
        </div>
        <div class="ml-5 text-base font-semibold text-gray-900">${fmt(onHandStorage)}</div>`,
    };
  }

  const monthIndex = Math.max(0, dataIndex - 1);
  const row = bufferRows[monthIndex];
  if (!row) return empty;

  const storage = row.storage || 0;
  const po = row.po || 0;
  const expectedDemand = row.expectedDemand || 0;
  const monthBuffer = row.buffer || 0;
  const finalStorage = storage + po - expectedDemand;

  const need = expectedDemand + monthBuffer;
  const surplus = (storage + po) - need;
  const verdictColor = surplus >= 0 ? '#10B981' : '#EF4444';
  const verdictLabel = surplus >= 0 ? 'Surplus' : 'Deficit';

  return {
    ...empty,
    html: `
      <div class="text-xs text-gray-500">
        Storage: ${dot('#1E3A8A')} Initial + ${dot('#3B82F6')} ${supplyTerm}
      </div>
      <div class="text-sm font-semibold text-gray-900 mb-2">
        ${fmt(storage)} + ${fmt(po)} = ${fmt(storage + po)}
      </div>
      <div class="text-xs text-gray-500">
        Need: ${dot('#9CA3AF')} ${demandTerm} + ${dot('#EF4444')} Buffer
      </div>
      <div class="text-sm font-semibold text-gray-900">
        ${fmt(expectedDemand)} + ${fmt(monthBuffer)} = ${fmt(need)}
      </div>
      <div class="mt-2 pt-2 border-t border-gray-200">
        <div class="text-xs font-medium text-gray-700">Final Storage: <span class="font-semibold">${fmt(finalStorage)}</span></div>
        <div class="mt-1.5 pt-1.5 border-t border-gray-200 text-xs font-semibold" style="color:${verdictColor}">${surplus >= 0 ? '▲' : '▼'} ${verdictLabel}: ${fmt(Math.abs(surplus))}</div>
      </div>`,
  };
}

// ---------- Logistics ----------
export function renderLogisticsTooltip(
  dataIndex: number,
  originalRow: any,
  isQuantity: boolean,
  matPrice: number,
  supplyTerm: string,
  demandTerm: string,
): TooltipResult {
  const fmt = (v: number) => {
    const raw = isQuantity ? v : v * matPrice;
    const abs = Math.abs(raw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return isQuantity ? abs : `$${abs}`;
  };
  const empty: TooltipResult = { labels: [], values: [], colors: [], formulas: [] };

  const isOnHand = dataIndex === 0;
  if (isOnHand) {
    const onHandStorage = originalRow['stock'] || 0;
    return {
      ...empty,
      html: `
        <div class="flex items-center gap-2">
          <div class="w-3 h-3 rounded-sm" style="background:#10B981"></div>
          <span class="text-sm font-medium text-gray-700">On-Hand Storage</span>
        </div>
        <div class="ml-5 text-base font-semibold text-gray-900">${fmt(onHandStorage)}</div>`,
    };
  }

  const initialStorage = originalRow['stock'] || 0;
  const supply = originalRow['POs'] || 0;
  const buffer = originalRow['emergencyStock'] || 0;
  const inventory = initialStorage + supply - buffer;
  const expectedDemand = originalRow['expectedDemand'] || 0;
  const requiredQuantity = originalRow['requiredQuantity'] || 0;
  const strategyPercent = originalRow['logisticAmnt'] || 0;
  const finalInventory = initialStorage + supply - expectedDemand;
  const surplus = inventory - requiredQuantity;
  const verdictColor = surplus >= 0 ? '#10B981' : '#EF4444';
  const verdictLabel = surplus >= 0 ? 'Surplus' : 'Deficit';

  return {
    ...empty,
    html: `
      <div class="text-xs text-gray-500">
        Inventory: ${dot('#1E3A8A')} Initial + ${dot('#3B82F6')} ${supplyTerm} − ${dot('#EF4444')} Buffer
      </div>
      <div class="text-sm font-semibold text-gray-900 mb-2">
        ${fmt(initialStorage)} + ${fmt(supply)} − ${fmt(buffer)} = ${fmt(inventory)}
      </div>
      <div class="text-xs text-gray-500">
        Required: ${dot('#9CA3AF')} ${demandTerm} × ${strategyPercent}%
      </div>
      <div class="text-sm font-semibold text-gray-900">
        ${fmt(expectedDemand)} × ${strategyPercent}% = ${fmt(requiredQuantity)}
      </div>
      <div class="mt-2 pt-2 border-t border-gray-200">
        <div class="text-xs font-medium text-gray-700">Final Inventory: <span class="font-semibold">${fmt(finalInventory)}</span></div>
        <div class="mt-1.5 pt-1.5 border-t border-gray-200 text-xs font-semibold" style="color:${verdictColor}">${surplus >= 0 ? '▲' : '▼'} ${verdictLabel}: ${fmt(Math.abs(surplus))}</div>
      </div>`,
  };
}

// ---------- Coverage ----------
export function renderCoverageTooltip(
  dataIndex: number,
  originalRow: any,
  isQuantity: boolean,
  matPrice: number,
  supplyTerm: string,
  demandTerm: string,
): TooltipResult {
  const fmt = (v: number) => {
    const raw = isQuantity ? v : v * matPrice;
    const abs = Math.abs(raw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return isQuantity ? abs : `$${abs}`;
  };
  const empty: TooltipResult = { labels: [], values: [], colors: [], formulas: [] };

  const isOnHand = dataIndex === 0;
  if (isOnHand) {
    const onHandStorage = originalRow['stock'] || 0;
    return {
      ...empty,
      html: `
        <div class="flex items-center gap-2">
          <div class="w-3 h-3 rounded-sm" style="background:#10B981"></div>
          <span class="text-sm font-medium text-gray-700">On-Hand Storage</span>
        </div>
        <div class="ml-5 text-base font-semibold text-gray-900">${fmt(onHandStorage)}</div>`,
    };
  }

  const initialStorage = originalRow['stock'] || 0;
  const supply = originalRow['POs'] || 0;
  const hedges = originalRow['financialPositions'] || 0;
  const buffer = originalRow['emergencyStock'] || 0;
  const physical = initialStorage + supply;
  const totalCoverage = physical + hedges;
  const expectedDemand = originalRow['expectedDemand'] || 0;
  const demandPlusBuffer = expectedDemand + buffer;
  const strategyPercent = originalRow['covRatio'] || 0;
  const reqCover = (demandPlusBuffer * strategyPercent) / 100;
  const surplus = totalCoverage - reqCover;
  const verdictColor = surplus >= 0 ? '#10B981' : '#EF4444';
  const verdictLabel = surplus >= 0 ? 'Surplus' : 'Deficit';
  const covPercentage = demandPlusBuffer > 0 ? Math.round((totalCoverage / demandPlusBuffer) * 100) : 0;

  return {
    ...empty,
    html: `
      <div class="text-xs text-gray-500">
        Coverage: ${dot('#10B981')} Physical + ${dot('#93C5FD')} Hedges
      </div>
      <div class="text-sm font-semibold text-gray-900 mb-2">
        ${fmt(physical)} + ${fmt(hedges)} = ${fmt(totalCoverage)}
      </div>
      <div class="text-xs text-gray-500">
        Required: (${dot('#9CA3AF')} ${demandTerm} + ${dot('#EF4444')} Buffer) × ${strategyPercent}%
      </div>
      <div class="text-sm font-semibold text-gray-900">
        (${fmt(expectedDemand)} + ${fmt(buffer)}) × ${strategyPercent}% = ${fmt(reqCover)}
      </div>
      <div class="mt-2 pt-2 border-t border-gray-200">
        <div class="text-xs font-medium text-gray-700">Coverage Ratio: <span class="font-semibold">${covPercentage}%</span></div>
        <div class="mt-1.5 pt-1.5 border-t border-gray-200 text-xs font-semibold" style="color:${verdictColor}">${surplus >= 0 ? '▲' : '▼'} ${verdictLabel}: ${fmt(Math.abs(surplus))}</div>
      </div>`,
  };
}

// ---------- Hedges Deriving ----------
// Reads directly from originalRow (= displayData[dataIndex]) which carries
// calculationDetails, percentages, and derivedExposures — single source of truth.
export function renderHedgesDerivingTooltip(
  originalRow: any,
  derivedMaterials: any[],
  datasets: DatasetConfig[],
  isQuantity: boolean,
): TooltipResult {
  const labels: string[] = [];
  const values: string[] = [];
  const colors: string[] = [];
  const formulas: string[] = [];

  const isOnHand = originalRow?.month === 'On-Hand';
  const details = originalRow?.calculationDetails || {};
  const derivedExposures = originalRow?.derivedExposures || {};
  const percentages = originalRow?.percentages || {};

  const fmtMoney = (v: number) =>
    `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  // On-Hand: show per-material inventory value from derivedExposures
  if (isOnHand) {
    let totalVal = 0;
    derivedMaterials.forEach((material: any, idx: number) => {
      const fc = material.formula_code;
      const val = derivedExposures[fc] || 0;
      const color = datasets.find(ds => ds.dataKey === fc)?.backgroundColor || '#6B7280';
      labels[idx] = material.formula_name;
      values[idx] = fmtMoney(val);
      colors[idx] = color;
      totalVal += val;
    });
    const footer = derivedMaterials.length > 1
      ? `<div class="text-sm font-medium text-gray-700">Total: <span class="font-semibold">${fmtMoney(totalVal)}</span></div>`
      : undefined;
    return { labels, values, colors, formulas, footer };
  }

  let totalMonetary = 0;
  const materialCodes = Object.keys(details);
  materialCodes.forEach((formulaCode, idx) => {
    const detail = details[formulaCode];
    const material = derivedMaterials.find((dm: any) => dm.formula_code === formulaCode);
    const percentage = percentages[formulaCode] || 0;
    if (!detail || !material) return;

    labels[idx] = `${material.formula_name} (${(percentage * 100).toFixed(0)}%)`;

    // Show raw exposure if clamped to 0 (supply > demand+buffer)
    const rawExposure = detail.demand + detail.buffer - detail.supply;
    const exposureStr = rawExposure < 0
      ? `${rawExposure.toFixed(0)} → 0 MT`
      : `${detail.finalExposure.toFixed(0)} MT`;
    values[idx] = `${fmtMoney(detail.monetaryScore)}  ·  ${exposureStr}`;

    const datasetColor = datasets.find(ds => ds.dataKey === formulaCode)?.backgroundColor || '#6B7280';
    colors[idx] = datasetColor;
    totalMonetary += detail.monetaryScore || 0;
  });

  const footer = materialCodes.length > 1
    ? `<div class="text-sm font-medium text-gray-700">Total: <span class="font-semibold">${fmtMoney(totalMonetary)}</span></div>`
    : undefined;

  return { labels, values, colors, formulas, footer };
}

// ---------- Base Material ----------
export function renderBaseMaterialTooltip(
  dataIndex: number,
  originalRow: any,
  datasets: DatasetConfig[],
  isQuantity: boolean,
  matPrice: number,
): TooltipResult {
  const fmt = (v: number) => {
    const raw = isQuantity ? v : v * matPrice;
    const abs = Math.abs(raw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return isQuantity ? abs : `$${abs}`;
  };
  const empty: TooltipResult = { labels: [], values: [], colors: [], formulas: [] };

  const isOnHand = dataIndex === 0;
  if (isOnHand) {
    // Build per-material on-hand breakdown
    const supplyDatasets = datasets.filter(ds => ds.dataKey.startsWith('supply_'));
    const parts = supplyDatasets
      .map(ds => {
        const code = ds.dataKey.replace('supply_', '');
        const val = originalRow?.supplyByMaterial?.[code] || 0;
        return { name: ds.label.replace('Supply: ', ''), color: ds.backgroundColor || '#6B7280', val };
      })
      .filter(p => p.val > 0);

    const totalOnHand = parts.reduce((s, p) => s + p.val, 0);
    const dotsLine = parts.map(p => `${dot(p.color)} ${p.name}`).join(' + ');
    const numsLine = parts.map(p => fmt(p.val)).join(' + ');

    return {
      ...empty,
      html: `
        <div class="flex items-center gap-2 mb-1">
          <div class="w-3 h-3 rounded-sm" style="background:#10B981"></div>
          <span class="text-sm font-medium text-gray-700">On-Hand Storage</span>
        </div>
        <div class="text-xs text-gray-500 ml-5">${dotsLine}</div>
        <div class="text-sm font-semibold text-gray-900 ml-5">${parts.length > 1 ? `${numsLine} = ` : ''}${fmt(totalOnHand)}</div>`,
    };
  }

  // Monthly row
  const supplyDatasets = datasets.filter(ds => ds.dataKey.startsWith('supply_'));
  const supplyParts = supplyDatasets
    .map(ds => {
      const code = ds.dataKey.replace('supply_', '');
      const val = originalRow?.supplyByMaterial?.[code] || 0;
      return { name: ds.label.replace('Supply: ', ''), color: ds.backgroundColor || '#6B7280', val };
    })
    .filter(p => p.val > 0);

  const totalSupply = originalRow?.totalSupply || 0;
  const hedges = originalRow?.hedges || 0;
  const totalCoverage = originalRow?.totalCoverage || 0;
  const totalDemand = originalRow?.totalDemand || 0;
  const buffer = originalRow?.buffer || 0;
  const fullDemand = originalRow?.fullDemand || totalDemand;
  const covPercentage = originalRow?.covPercentage || 0;
  const hedgedPercentage = originalRow?.hedgedPercentage || 0;
  const physicalGap = fullDemand - totalSupply;
  const finalGap = fullDemand - totalCoverage;
  const physicalGapColor = physicalGap > 0 ? '#EF4444' : '#10B981';
  const finalGapColor = finalGap > 0 ? '#EF4444' : '#10B981';

  // Physical supply line: ■ Mat1 + ■ Mat2
  const supplyDots = supplyParts.map(p => `${dot(p.color)} ${p.name}`).join(' + ');
  const supplyNums = supplyParts.map(p => fmt(p.val)).join(' + ');

  // Demand + buffer line
  const demandLine = buffer > 0
    ? `${fmt(totalDemand)} + ${fmt(buffer)} = ${fmt(fullDemand)}`
    : fmt(totalDemand);

  return {
    ...empty,
    html: `
      <div class="text-xs text-gray-500">
        ${dot('#EF4444')} Demand + Buffer (base equiv.)
      </div>
      <div class="text-sm font-semibold text-gray-900">
        ${demandLine}
      </div>
      <div class="text-xs text-gray-500 mt-1.5">
        Physical Supply: ${supplyDots}
      </div>
      <div class="text-sm font-semibold text-gray-900">
        ${supplyNums}${supplyParts.length > 1 ? ` = ${fmt(totalSupply)}` : ''}
      </div>
      <div class="mt-1.5 pt-1.5 border-t border-gray-200">
        <span class="text-xs text-gray-500">Physical Gap:</span>
        <span class="text-xs font-semibold" style="color:${physicalGapColor}">${physicalGap > 0 ? `−${fmt(physicalGap)}` : `+${fmt(Math.abs(physicalGap))}`}</span>
        <span class="text-xs text-gray-400 ml-1">(${covPercentage}% covered)</span>
      </div>${hedges > 0 ? `
      <div class="mt-1.5 pt-1.5 border-t border-gray-200">
        <span class="text-xs text-gray-500">${dot('#93C5FD')} Hedges:</span>
        <span class="text-xs font-semibold" style="color:#3B82F6">+${fmt(hedges)}</span>
        <span class="text-xs text-gray-400 ml-1">(+${hedgedPercentage}%)</span>
      </div>` : ''}
      <div class="mt-1.5 pt-1.5 border-t border-gray-200">
        <span class="text-xs font-semibold" style="color:${finalGapColor}">${finalGap > 0 ? `▼ Unhedged: −${fmt(finalGap)}` : `▲ Fully Covered +${fmt(Math.abs(finalGap))}`}</span>
        <span class="text-xs text-gray-400 ml-1">(${covPercentage + hedgedPercentage}% total)</span>
      </div>`,
  };
}

// ---------- Default / Fallback ----------
export function renderDefaultTooltip(
  tooltipModel: any,
  isQuantity: boolean,
): TooltipResult {
  const labels = tooltipModel.dataPoints.map((dp: any) => dp.dataset.label || '');
  const values = tooltipModel.dataPoints.map((dp: any) => {
    const rawValue = dp.raw as number;
    if (rawValue === null || rawValue === undefined) return '0';
    const formatted = Math.abs(rawValue).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const sign = rawValue < 0 ? '-' : '';
    return isQuantity ? `${sign}${formatted}` : `${sign}$${formatted}`;
  });
  const colors = tooltipModel.dataPoints.map((dp: any) => {
    const color = dp.dataset.borderColor;
    return typeof color === 'string' ? color : '#6B7280';
  });

  return { labels, values, colors, formulas: [] };
}
